/**
 * Where the mission's own kit is, right now.
 *
 * The run panel already knows where each assigned device was as of the last
 * monitor tick. That is up to a minute old, deliberately: the deviation state
 * machine wants settled positions rather than every jitter, and it only writes
 * a row once a mission is under way. A map wants the opposite — the newest fix
 * available, for every assigned device, whether or not the run has started.
 *
 * So this reads both. The location stream COP draws from says where a device
 * is; the monitor says what that position means — on route, off route, signal
 * lost. Neither answers the other's question, and showing a pin without the
 * verdict would be a dot on a map that an operator has to correlate by hand.
 */
import { useEffect, useMemo, useState } from 'react';
import { SSEDeviceLocation, sseLocationService } from '../../services/sseLocationService';
import {
  Mission,
  MissionAssignee,
  MissionDevicePosition,
  MissionDeviceState,
  listMissionDevicePositions,
} from '../../services/missionApi';

export type MissionDeviceKind = NonNullable<MissionAssignee['device_kind']>;

export interface MissionDeviceTrack {
  deviceId: string;
  label: string;
  kind: MissionDeviceKind;
  /** Only observers collect RF, which is what makes a device able to see a follower. */
  isObserver: boolean;
  /** A fixed collection site: shown on the map, never treated as the convoy. */
  isStationary: boolean;
  latitude: number;
  longitude: number;
  /** Degrees from north. Absent on fixes the monitor supplied. */
  heading: number | null;
  speedMps: number | null;
  /** When the fix was taken, as reported. */
  at: string | null;
  /** Minutes since that fix, recomputed on a timer so a pin ages in place. */
  ageMinutes: number;
  /** Pushed by the location stream, or the monitor's last known position. */
  source: 'stream' | 'monitor';
  routeState: MissionDeviceState['route_state'];
  signalState: MissionDeviceState['signal_state'];
  /** Perpendicular distance from the planned line, when the monitor has measured one. */
  offRouteM: number | null;
  /** Tracker has Live video/audio on. Room is keyed by the published device id. */
  isStreaming: boolean;
  streamDeviceId: string | null;
}

/** Ages held fixes so a pin fades on its own, without a new fix to trigger it. */
const AGE_TICK_MS = 20000;
/**
 * Last-known from last week is not a live mission picture. A device that
 * has not published inside this window stays on the roster and off the map.
 */
export const MAP_MAX_AGE_MINUTES = 24 * 60;

/**
 * Fix times arrive in two shapes and only one of them carries a zone.
 *
 * The location stream sends `YYYY-MM-DD HH:mm:ss` in local time; the mission
 * API sends ISO with an offset. Handing the first to `Date` unchanged is parsed
 * as UTC by some engines and local by others, and being wrong about that is a
 * whole-timezone error in how old a fix looks — which is exactly the number
 * this drives the pin's opacity from.
 */
export function parseFixTime(value?: string | null): number | null {
  if (!value) return null;
  const at = new Date(value.includes('T') ? value : value.replace(' ', 'T')).getTime();
  return Number.isFinite(at) ? at : null;
}

/**
 * Merge a roster, the monitor's last word and the newest streamed fixes.
 *
 * Pure, and separate from the hook, because every awkward case here is a
 * question about which of two sources to believe — and those are worth testing
 * without standing up a subscription to find out.
 */
/**
 * Find the streamed fix for an assignee.
 *
 * The assignee is `phone_app_id`. The location stream often publishes the
 * serial (or another alias) and only agrees on `entity_id`. COP already
 * treats those as the same handset; matching only the assignee string is
 * why a live phone can sit on COP and never on the mission map.
 */
const KIND_FAMILY: Record<string, string> = {
  phone: 'phone',
  cellphone: 'phone',
  samsungphone: 'phone',
  smartphone: 'phone',
  mobile: 'phone',
  wardriver: 'wardriver',
  beluga: 'wardriver',
  watch: 'watch',
  vehicle: 'vehicle',
  drone: 'drone',
};

export function kindFamily(value?: string | null): string {
  return KIND_FAMILY[(value || '').toLowerCase()] || (value || '').toLowerCase();
}

/** Same radio, different label: Beluga GPS often arrives as Phone. */
export function kindsCompatible(wanted?: string | null, got?: string | null): boolean {
  if (!wanted || !got) return true;
  const a = kindFamily(wanted);
  const b = kindFamily(got);
  if (a === b) return true;
  return (a === 'phone' && b === 'wardriver') || (a === 'wardriver' && b === 'phone');
}

function sameId(left?: string | null, right?: string | null): boolean {
  return !!left && !!right && left.toLowerCase() === right.toLowerCase();
}

export function matchStreamedFix(
  assignee: MissionAssignee,
  fixes: Record<string, SSEDeviceLocation>,
): SSEDeviceLocation | undefined {
  const direct = fixes[assignee.device_id]
    || Object.values(fixes).find((fix) => sameId(fix.device_id, assignee.device_id));
  if (direct) return direct;
  const entity = assignee.entity_id;
  if (!entity) return undefined;
  const wanted = assignee.device_kind;
  let best: SSEDeviceLocation | undefined;
  Object.values(fixes).forEach((fix) => {
    if (!sameId(fix.entity_id, entity)) return;
    const got = fix.device_type || fix.source;
    if (!kindsCompatible(wanted, got)) return;
    const incoming = parseFixTime(fix.timestamp) ?? 0;
    const held = parseFixTime(best?.timestamp) ?? 0;
    if (!best || incoming >= held) best = fix;
  });
  return best;
}

export function locationBelongsToMission(
  location: SSEDeviceLocation,
  assignees: Map<string, MissionAssignee>,
): boolean {
  if (assignees.has(location.device_id)) return true;
  for (const assignee of assignees.values()) {
    if (sameId(assignee.device_id, location.device_id)) return true;
  }
  const entity = location.entity_id;
  if (!entity) return false;
  const got = location.device_type || location.source;
  for (const assignee of assignees.values()) {
    if (!sameId(assignee.entity_id, entity)) continue;
    if (kindsCompatible(assignee.device_kind, got)) return true;
  }
  return false;
}

export function mergePositionSources(
  monitor: MissionDeviceState[],
  tracks: MissionDevicePosition[],
): MissionDeviceState[] {
  const byId = new Map<string, MissionDeviceState>();
  tracks.forEach((row) => {
    byId.set(row.device_id.toLowerCase(), {
      mission_id: 0,
      device_id: row.device_id,
      route_state: 'unknown',
      signal_state: 'unknown',
      consecutive_off: 0,
      latitude: row.latitude,
      longitude: row.longitude,
      last_fix_at: row.last_fix_at || null,
      changed_at: row.last_fix_at || '',
      updated_at: row.last_fix_at || '',
    });
  });
  monitor.forEach((row) => {
    const held = byId.get(row.device_id.toLowerCase());
    if (!held) {
      byId.set(row.device_id.toLowerCase(), row);
      return;
    }
    const monitorAt = parseFixTime(row.last_fix_at);
    const heldAt = parseFixTime(held.last_fix_at);
    const monitorNewer = monitorAt != null && (heldAt == null || monitorAt >= heldAt);
    byId.set(row.device_id.toLowerCase(), {
      ...held,
      ...row,
      latitude: monitorNewer ? (row.latitude ?? held.latitude) : (held.latitude ?? row.latitude),
      longitude: monitorNewer ? (row.longitude ?? held.longitude) : (held.longitude ?? row.longitude),
      last_fix_at: monitorNewer ? (row.last_fix_at || held.last_fix_at) : (held.last_fix_at || row.last_fix_at),
    });
  });
  return Array.from(byId.values());
}

export function buildTracks(
  assignees: MissionAssignee[],
  states: MissionDeviceState[],
  fixes: Record<string, SSEDeviceLocation>,
  now: number,
): MissionDeviceTrack[] {
  const byDevice = new Map<string, MissionDeviceState>();
  states.forEach((s) => byDevice.set(s.device_id.toLowerCase(), s));

  const tracks: MissionDeviceTrack[] = [];
  assignees.forEach((assignee) => {
    const deviceId = assignee.device_id;
    const state = byDevice.get(deviceId.toLowerCase());
    const fix = matchStreamedFix(assignee, fixes);
    const fixAt = parseFixTime(fix?.timestamp);
    const stateAt = parseFixTime(state?.last_fix_at);
    // The monitor reads the same track table the stream publishes from, so the
    // two agree far more often than not. Where they differ it is because one
    // has seen a fix the other has not, and the newer one is right by
    // construction.
    const streaming = !!fix && (stateAt == null || (fixAt ?? 0) >= stateAt);

    const latitude = streaming ? fix.latitude : state?.latitude;
    const longitude = streaming ? fix.longitude : state?.longitude;
    // An assignee with no position anywhere is left off rather than pinned at
    // (0, 0). "We do not know where this is" belongs in the roster list, not
    // as a marker in the Gulf of Guinea.
    if (!Number.isFinite(latitude as number) || !Number.isFinite(longitude as number)) return;

    const at = streaming ? fix.timestamp : state?.last_fix_at || null;
    const atMs = streaming ? fixAt : stateAt;
    const ageMinutes = atMs == null
      ? Number.POSITIVE_INFINITY
      : Math.max(0, (now - atMs) / 60000);
    // No timestamp, or one older than a day: not a live picture. The roster
    // still lists the assignee; the map does not invent a current location.
    if (!Number.isFinite(ageMinutes) || ageMinutes > MAP_MAX_AGE_MINUTES) return;

    tracks.push({
      deviceId,
      label: assignee.label || state?.label || deviceId,
      kind: assignee.device_kind || 'phone',
      isObserver: !!assignee.is_observer,
      isStationary: !!assignee.is_stationary,
      latitude: latitude as number,
      longitude: longitude as number,
      heading: streaming ? fix.heading ?? null : null,
      speedMps: streaming ? fix.speed ?? null : state?.speed_mps ?? null,
      at,
      ageMinutes,
      source: streaming ? 'stream' : 'monitor',
      routeState: state?.route_state || 'unknown',
      signalState: state?.signal_state || 'unknown',
      offRouteM: state?.distance_m ?? null,
      isStreaming: !!fix?.is_streaming,
      streamDeviceId: fix?.is_streaming ? fix.device_id : null,
    });
  });
  return tracks.sort((a, b) => a.label.localeCompare(b.label));
}

export interface UseMissionDeviceTracksOptions {
  /** Off while another view owns the map, so an unseen page holds no subscription. */
  enabled?: boolean;
}

/**
 * Live positions for a mission's assigned devices.
 *
 * Returns one track per assignee that has a position from either source, newest
 * fix winning. An assignee with no position anywhere is omitted rather than
 * pinned at (0, 0) — "we do not know where this is" is a fact for the roster
 * list to state, not for the map to invent a location for.
 */
export function useMissionDeviceTracks(
  mission: Mission | null | undefined,
  deviceStates?: MissionDeviceState[] | null,
  options: UseMissionDeviceTracksOptions = {},
): MissionDeviceTrack[] {
  const assignees = useMemo(() => {
    const out = new Map<string, MissionAssignee>();
    (mission?.assignees || []).forEach((a) => out.set(a.device_id, a));
    return out;
  }, [mission?.assignees]);

  const enabled = options.enabled !== false && assignees.size > 0;
  const missionId = mission?.id ?? null;

  const [fixes, setFixes] = useState<Record<string, SSEDeviceLocation>>({});
  const [trackRows, setTrackRows] = useState<MissionDevicePosition[]>([]);
  const [now, setNow] = useState(() => Date.now());

  // A device can be on two missions in a week. Holding its last fix across a
  // change of mission would draw yesterday's position against today's roster.
  useEffect(() => { setFixes({}); setTrackRows([]); }, [missionId]);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsubscribe = sseLocationService.onLocation((location) => {
      // Filtered in the handler rather than at render: the stream carries the
      // whole fleet, and a mission is a handful of devices. Holding the rest
      // would re-render the map on every fix belonging to somebody else.
      if (!locationBelongsToMission(location, assignees)) return;
      setFixes((prev) => {
        const held = prev[location.device_id];
        const heldAt = parseFixTime(held?.timestamp);
        const incomingAt = parseFixTime(location.timestamp);
        // The stream replays its cache on connect, so out-of-order delivery is
        // routine rather than exceptional. An older fix must not win.
        if (heldAt != null && incomingAt != null && incomingAt < heldAt) return prev;
        return { ...prev, [location.device_id]: location };
      });
    });
    // Deliberately not forced: COP may already hold this connection, and
    // tearing it down to re-request the same stream would blank its map.
    sseLocationService.connect();
    // Nor is it disconnected on the way out, for the same reason.
    return unsubscribe;
  }, [enabled, assignees]);

  useEffect(() => {
    if (!enabled || missionId == null) return undefined;
    let cancelled = false;
    const load = () => {
      listMissionDevicePositions(missionId)
        .then((rows) => { if (!cancelled) setTrackRows(rows); })
        .catch((err) => {
          console.warn('[missions] device-positions failed', err);
          if (!cancelled) setTrackRows([]);
        });
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [enabled, missionId]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => window.clearInterval(timer);
  }, [enabled]);

  const roster = useMemo(() => Array.from(assignees.values()), [assignees]);

  const mergedStates = useMemo(
    () => mergePositionSources(deviceStates || [], trackRows),
    [deviceStates, trackRows],
  );

  return useMemo(
    () => buildTracks(roster, mergedStates, fixes, now),
    [roster, mergedStates, fixes, now],
  );
}

export default useMissionDeviceTracks;
